import { batch } from 'solid-js'
import { clientToWorld } from '../canvas/viewportRef.ts'
import { setEditingTextId, setMarquee } from '../editor.ts'
import { nodeBounds, normalizeRect, rectsIntersect } from '../scene/geometry.ts'
import {
  abortCommit,
  beginCommit,
  clearSelection,
  endCommit,
  scene,
  select,
  selection,
  updateNode,
  withGroups,
} from '../scene/store.ts'
import type { NodeId, Point } from '../scene/types.ts'
import { startDrag } from '../util/drag.ts'
import type { PointerCtx, Tool } from './types.ts'

/** Screen pixels of movement before a press counts as a drag. */
const DRAG_THRESHOLD = 3

export function startMove(start: Point, ids: NodeId[]): void {
  const origin = ids.map((id) => {
    const n = scene.nodes[id]!
    return { id, x: n.x, y: n.y }
  })
  beginCommit(ids)
  let moved = false

  startDrag({
    onMove(e) {
      const p = clientToWorld(e)
      let dx = p.x - start.x
      let dy = p.y - start.y
      if (!moved && Math.hypot(dx, dy) * scene.camera.z < DRAG_THRESHOLD) return
      moved = true
      if (e.shiftKey) {
        if (Math.abs(dx) > Math.abs(dy)) dy = 0
        else dx = 0
      }
      batch(() => {
        for (const o of origin) updateNode(o.id, { x: o.x + dx, y: o.y + dy })
      })
    },
    onUp() {
      if (moved) endCommit('Move')
      else abortCommit()
    },
    onCancel() {
      batch(() => {
        for (const o of origin) updateNode(o.id, { x: o.x, y: o.y })
      })
      abortCommit()
    },
  })
}

function startMarquee(ctx: PointerCtx): void {
  const start = ctx.world
  const base = ctx.event.shiftKey ? selection() : []

  startDrag({
    onMove(e) {
      const r = normalizeRect(start, clientToWorld(e))
      setMarquee(r)
      const hits = scene.order.filter((id) => {
        const n = scene.nodes[id]
        return !!n && !n.locked && !n.hidden && rectsIntersect(nodeBounds(n), r)
      })
      select([...new Set([...base, ...withGroups(hits)])])
    },
    onUp() {
      setMarquee(null)
    },
    onCancel() {
      setMarquee(null)
      select(base)
    },
  })
}

export const selectTool: Tool = {
  cursor: 'default',

  onPointerDown(ctx) {
    const node = ctx.nodeId ? scene.nodes[ctx.nodeId] : undefined

    if (!node || node.locked) {
      if (!ctx.event.shiftKey) clearSelection()
      startMarquee(ctx)
      return
    }

    const group = withGroups([node.id])
    if (ctx.event.shiftKey) {
      const sel = selection()
      select(
        sel.includes(node.id)
          ? sel.filter((id) => !group.includes(id))
          : [...new Set([...sel, ...group])],
      )
    } else if (!selection().includes(node.id)) {
      select(group)
    }

    const ids = selection()
    if (ids.length > 0) startMove(ctx.world, ids)
  },

  onDoubleClick(ctx) {
    const node = ctx.nodeId ? scene.nodes[ctx.nodeId] : undefined
    if (node?.type === 'text' && !node.locked) setEditingTextId(node.id)
  },
}
